import { useState, useMemo, useEffect } from 'react';
import { StepWizardChildProps } from 'react-step-wizard';
import classNames from 'classnames';
import { faDotCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/free-regular-svg-icons';

import { useAuthContext } from 'contexts/AuthContext';
import { NSBadge, NSButton, NSDropdown } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import useProFormaModels from 'queries/ProForma/useProFormaModels';
import { IOptionAnyValue } from 'bricks/types';
import NSRadioCard from 'components/NSRadioCard/NSRadioCard';
import { useGetBackOfEnvelopeScenarios } from 'views/DealDashboard/BOE/hooks/useGetBOEScenarios';
import { useGetBackOfEnvelope } from 'views/DealDashboard/BOE/hooks/useGetBOE';
import { useSlideout } from 'contexts/BackOfEnvelopeContext';
import { ProFormaModelIds } from 'constants/proForma';
import { DEAL_MIXED_USE_RESIDENTIAL_OVER_RETAIL } from 'views/ProFormaTable/constants';
import ProFormaStep1StatCard from './ProFormaStep1StatCard/ProFormaStep1StatCard';
import useAssetTypeDropdownOptions from 'hooks/useDisabledAssetTypesProps';

export interface ICreateProFormaStep1Props extends Partial<StepWizardChildProps> {
    toggle: () => void;
    dealId: number;
    initialScenario?: IOptionAnyValue;
    proFormaModelId: number | undefined;
    setProFormaModelId: (id: number) => void;
    selectedScenario?: IOptionAnyValue;
    setSelectedScenario: (scenario: IOptionAnyValue | undefined) => void;
    handleCreateProForma: () => void;
    isCreatingProForma: boolean;
    dealAssetTypeCorrelationId?: string;
}

const CreateProFormaStep1 = ({
    goToStep, toggle, dealId, initialScenario, proFormaModelId, setProFormaModelId,
    selectedScenario, setSelectedScenario, handleCreateProForma, isCreatingProForma,
    dealAssetTypeCorrelationId,
}: ICreateProFormaStep1Props) => {
    const { selectedOrganizationId, user } = useAuthContext();

    const shouldDefaultToApartmentWithRetail = dealAssetTypeCorrelationId === DEAL_MIXED_USE_RESIDENTIAL_OVER_RETAIL;
    const [shouldStartFromBackOfEnvelope, setShouldStartFromBackOfEnvelope] = useState(!shouldDefaultToApartmentWithRetail);

    const { data: assetTypes } = useProFormaModels();
    const isNorthspyreUser = user?.email?.toLowerCase().endsWith('@northspyre.com');
    const assetTypeDropdownOptions = useAssetTypeDropdownOptions(assetTypes || []);

    const handleSelectAssetType = (option: IOptionAnyValue) => {
        setProFormaModelId(option?.value);
        setSelectedScenario(undefined);
        setShouldStartFromBackOfEnvelope(false);
    };

    // Add BOE scenarios query
    const { data: scenariosData, refetch: refetchScenarios } = useGetBackOfEnvelopeScenarios({ orgId: selectedOrganizationId!, dealId });
    const [latestScenarios, setLatestScenarios] = useState<typeof scenariosData | null>(null);

    const { data: fullSelectedScenario } = useGetBackOfEnvelope({
        orgId: selectedOrganizationId!,
        scenarioId: selectedScenario?.value,
        enabled: !!selectedScenario?.value && shouldStartFromBackOfEnvelope,
    });

    const {
        toggleSlideout, getSlideoutValues, setSelectedSlideoutScenarioId, setSelectedScenario: setSelectedScenarioForSlideout,
        slideoutSavedAt, 
    } = useSlideout();

    const scenarioDropdownOptions = useMemo(() => {
        const dataSource = latestScenarios ?? scenariosData;
        if (dataSource?.items?.length) {
            return dataSource.items.map(scenario => ({
                value: scenario.id,
                label: scenario.name,
                disabled: false,
                toggle: true,
            }));
        }
        return [
            {
                label: 'A Back-of-Envelope scenario needs to be created first',
                component: (
                    <span
                        role="button"
                        tabIndex={0}
                        className="text-primary cursor--pointer"
                        onClick={() => toggleSlideout(undefined, dealId)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                toggleSlideout(undefined, dealId);
                            }
                        }}
                    >
                        A Back-of-Envelope scenario needs to be created first
                    </span>
                ),
                value: 'no-back-of-envelope',
            },
        ];
    }, [scenariosData, latestScenarios, toggleSlideout, dealId]);
    // Handle scenario updates when scenarios are deleted or changed
    useEffect(() => {
        if (!scenariosData?.items) return;
        if (!scenariosData.items.length) {
            setSelectedScenario(undefined);
            return;
        }

        const currentScenarioExists = selectedScenario && scenariosData.items.some(scenario => scenario.id === selectedScenario.value);
        if (!currentScenarioExists && initialScenario) {
            setSelectedScenario({ label: scenariosData.items[0].name, value: scenariosData.items[0].id });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scenariosData?.items, initialScenario]);

    useEffect(() => {
        if (fullSelectedScenario?.propertyTypeId && shouldStartFromBackOfEnvelope) {
            const propertyTypeId = fullSelectedScenario.propertyTypeId === ProFormaModelIds.APARTMENT_WITH_RETAIL && !isNorthspyreUser
                ? ProFormaModelIds.APARTMENT
                : fullSelectedScenario.propertyTypeId;
            setProFormaModelId(propertyTypeId);
        }
        if (!shouldStartFromBackOfEnvelope && shouldDefaultToApartmentWithRetail) {
            setProFormaModelId(isNorthspyreUser
                ? ProFormaModelIds.APARTMENT_WITH_RETAIL
                : ProFormaModelIds.APARTMENT);
            setSelectedScenario(undefined);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullSelectedScenario, shouldStartFromBackOfEnvelope, setProFormaModelId, shouldDefaultToApartmentWithRetail, isNorthspyreUser]);

    const chooseStartingSelection = (startFromBoE: boolean) => {
        if (!startFromBoE) setSelectedScenario(undefined);
        setShouldStartFromBackOfEnvelope(startFromBoE);
    };

    const goToNextStep = () => {
        handleCreateProForma();
    };

    const shouldDisableConfirm = (shouldStartFromBackOfEnvelope && !selectedScenario) || (!shouldStartFromBackOfEnvelope && !proFormaModelId);

    const handleSelectScenario = (option: IOptionAnyValue) => {
        if (option?.value === 'no-back-of-envelope') {
            return;
        }
        setSelectedScenario(option);
    };


    const simplifiedOverviewScenarios = scenariosData?.items?.map(item => ({ value: item.id, label: item.name }));

    useEffect(() => {
        if (latestScenarios) setLatestScenarios(null);
    }, [scenariosData?.items, latestScenarios]);

    const handleToggleSlideout = () => {
        if (simplifiedOverviewScenarios && selectedScenario?.value) {
            setSelectedSlideoutScenarioId(selectedScenario.value);
            setSelectedScenarioForSlideout(selectedScenario);
            getSlideoutValues();
        }
        toggleSlideout(selectedScenario?.value);
    };

    useEffect(() => {
        if (!slideoutSavedAt) return;

        const doRefetch = async () => {
            if (refetchScenarios) {
                const { data } = await refetchScenarios();
                if (data?.items) {
                    setLatestScenarios(data);
                }
            }
        };

        void doRefetch();
    }, [slideoutSavedAt, refetchScenarios]);

    return (
        <>
            <NSRadioCard
                cardClassName={classNames('p-2 mb-2 NSCard--level-4', {
                    'border border-primary': shouldStartFromBackOfEnvelope,
                    'border border-secondary': !shouldStartFromBackOfEnvelope,
                })}
                onClick={() => chooseStartingSelection(true)}
            >
                <div className="d-flex justify-content-between">
                    <NSBadge color="primary-lighten">Recommended</NSBadge>

                    <RenderIf isTrue={shouldStartFromBackOfEnvelope}>
                        <FontAwesomeIcon icon={faDotCircle} className="text-primary NSRadioCard__radio-btn--selected" />
                    </RenderIf>
                    <RenderIf isTrue={!shouldStartFromBackOfEnvelope}>
                        <FontAwesomeIcon icon={faCircle} />
                    </RenderIf>
                </div>
                <div className="text-white mb-1 mt-1">Start your pro forma from an existing Back-Of-Envelope scenario</div>

                <NSDropdown
                    isFullWidth
                    options={scenarioDropdownOptions}
                    selectedOption={selectedScenario}
                    handleSelectOption={handleSelectScenario}
                    placeholder="Select scenario"
                    disableOnSingleOption={false}
                />

                <RenderIf isTrue={Boolean(selectedScenario)}>
                    <hr className="my-1" />

                    <div className="d-flex justify-content-between">
                        <ProFormaStep1StatCard cardClassName="mr-1" />
                        <ProFormaStep1StatCard cardClassName="mr-1" />
                        <ProFormaStep1StatCard />
                    </div>

                    <div className="d-flex text-primary cursor--pointer mt-1" role="presentation" onClick={handleToggleSlideout}>
                        <small className="my-auto">View scenario in Back-Of-Envelope</small>
                    </div>
                </RenderIf>
            </NSRadioCard>

            <NSRadioCard
                cardClassName={classNames('p-2 mb-2 NSCard--level-4', {
                    'border border-primary': !shouldStartFromBackOfEnvelope,
                    'border border-secondary': shouldStartFromBackOfEnvelope,
                })}
                onClick={() => chooseStartingSelection(false)}
                dataTestId="start-from-scratch-option"
            >
                <div className="d-flex justify-content-between">
                    <div className="text-white mb-1 my-auto">Start from scratch</div>

                    <RenderIf isTrue={!shouldStartFromBackOfEnvelope}>
                        <FontAwesomeIcon icon={faDotCircle} className="text-primary my-auto NSRadioCard__radio-btn--selected" />
                    </RenderIf>
                    <RenderIf isTrue={shouldStartFromBackOfEnvelope}>
                        <FontAwesomeIcon icon={faCircle} className="my-auto" />
                    </RenderIf>
                </div>

                <RenderIf isTrue={!shouldStartFromBackOfEnvelope}>
                    <div className="h6 mb-0">
                        <span className="text-warning">Note:</span>
                        {' '}
                        Currently, Pro Forma creation is only available for the asset classes
                        <br />
                        below. If you do not see your asset class, please upload a pro forma instead.
                    </div>
                    <hr className="my-1" />
                    <div className="text-white my-auto d-flex flex-row text-nowrap align-items-center">
                        Asset type:
                        &nbsp;
                        <NSDropdown
                            options={assetTypeDropdownOptions}
                            selectedOption={assetTypeDropdownOptions.find(option => option.value === String(proFormaModelId))}
                            handleSelectOption={handleSelectAssetType}
                            placeholder="Select asset type"
                            containerClassName="w-50"
                            toggleClassName="w-50"
                            dataTestId="asset-type-dropdown"
                        />
                    </div>
                </RenderIf>
            </NSRadioCard>

            <hr />

            <div className="d-flex float-right my-0">
                <NSButton outline callback={toggle} text="Cancel" className="btn-secondary mr-2" disabled={false} />
                <NSButton
                    outline={false}
                    callback={goToNextStep}
                    className="btn-primary btn-block"
                    text="Confirm"
                    disabled={shouldDisableConfirm}
                    isLoading={isCreatingProForma}
                    dataTestId="confirm-pro-forma-button"
                />
            </div>
        </>
    );
};

export default CreateProFormaStep1;
